import toast from 'react-hot-toast';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import deleteRequest from './deleteRequest';
import getRequest from './getRequest';
import patchRequest from './patchRequest';
import postRequest from './postRequest';
import updateRequest from './updateRequest';
import axios from 'axios';
import {
  refreshAuthSession,
  resetRefreshSessionFlight,
} from 'src/utils/authRefresh';
import { clearAuthSessionStorage } from 'src/utils/authTokens';
import { isGuestAuthRoute } from 'src/utils/guestAuthRoutes';

const axiosInstance = axios.create();

type RetriableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  /** When true, 401 is returned to the caller (no refresh / login redirect). */
  skipAuthRedirect?: boolean;
};

axiosInstance.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem('accessToken');
    config.headers.Authorization = accessToken ? `Bearer ${accessToken}` : '';
    config.headers.accept = '*/*';
    return config;
  },
  (error) => Promise.reject(error)
);

let isUnauthorizedToastShown = false;

export const resetUnauthorizedToastFlag = (): void => {
  isUnauthorizedToastShown = false;
};

const handleRefreshAuthFailure = (error: AxiosError): Promise<never> => {
  resetRefreshSessionFlight();
  clearAuthSessionStorage();

  const onLoginPage = isGuestAuthRoute(globalThis.location.pathname);
  if (!onLoginPage) {
    globalThis.location.replace('/login');
  }

  if (!isUnauthorizedToastShown && !onLoginPage) {
    toast.error('Session expired. Please log in.');
    isUnauthorizedToastShown = true;
  }

  return Promise.reject(error);
};

const isKnowledgeBaseRequest = (url?: string): boolean => {
  if (!url) return false;
  return url.includes('/api/kb') || url.includes('api.mindtime.eu/api/kb');
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined;

    if (isKnowledgeBaseRequest(originalRequest?.url)) {
      throw error;
    }

    if (
      error?.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.skipAuthRedirect
    ) {
      throw error;
    }

    const isRefreshRequest = originalRequest.url?.includes(
      '/user/refresh-token'
    );
    if (isRefreshRequest) {
      return handleRefreshAuthFailure(error);
    }

    if (!localStorage.getItem('refreshToken')) {
      return handleRefreshAuthFailure(error);
    }

    originalRequest._retry = true;

    try {
      const tokens = await refreshAuthSession();
      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
      return await axiosInstance(originalRequest);
    } catch (refreshError) {
      return handleRefreshAuthFailure(refreshError as AxiosError);
    }
  }
);

export default axiosInstance;
export { deleteRequest, getRequest, patchRequest, postRequest, updateRequest };
